import { useCallback, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { IconX } from '@tabler/icons-react';
import type { PropertySearchQuery } from 'components/marketplace/helpers/useSearchQueries';
import type { SavedSearchFieldsFragment } from 'components/marketplace/savedSearches/__generated__/saved-searches.zeus';
import { SavedSearchesDropdown } from 'components/marketplace/savedSearches/SavedSearchesDropdown';
import { SaveSearchButton } from 'components/marketplace/savedSearches/SaveSearchButton';
import { SearchFilters } from 'components/marketplace/search/SearchFilters';
import type { SearchFiltersProps } from 'components/marketplace/search/SearchFilters';

interface MapSearchFiltersProps extends SearchFiltersProps {
  onClose?: () => void;
  onApplyFilters?: () => void;
  onResetFilters?: () => void;
  isLoading: boolean;
  /**
   * The canonical query passed to `SaveSearchButton`. Includes location
   * fields that `useSearchState.getCanonical()` intentionally omits. When
   * unset, the save action is hidden (the host page hasn't opted in yet).
   */
  saveSearchQuery?: PropertySearchQuery;
  /**
   * When provided, renders the saved-searches switcher at the top of the panel.
   * Used by both the mobile and desktop filters panels, since the QuickFilters
   * bar (which normally hosts the switcher) is hidden while the filters panel
   * is open.
   */
  onApplySavedSearch?: (savedSearch: SavedSearchFieldsFragment) => void;
  /**
   * The saved search the current results came from, if any. Drives the save
   * button's update-vs-save-new flow and the switcher's "Active" marker.
   */
  activeSavedSearch?: SavedSearchFieldsFragment | null;
  /** Display label for the active location, for better default names. */
  locationLabel?: string | null;
  /** Called with the new id after a save so the host can mark it active. */
  onSavedSearchCreated?: (savedSearchId: string) => void;
}

export const MapSearchFilters = (props: MapSearchFiltersProps) => {
  // Dropdown in bricks-next only reads prefilledValue on mount (uncontrolled).
  // We use a key to force re-render when filters reset OR when QuickFilters
  // updates bed/bath values externally.
  const [resetFiltersKey, setResetFiltersKey] = useState(0);
  const handleResetFilters = () => {
    setResetFiltersKey((prev) => prev + 1);
    props.onResetFilters?.();
  };

  const searchDisabled = props?.errors ? Object.keys(props.errors).length > 0 : false;

  const handleApplySavedSearch = useCallback(
    (savedSearch: SavedSearchFieldsFragment) => {
      props.onApplySavedSearch?.(savedSearch);
      // Mobile filters are fullscreen; closing on select returns the user to
      // results while the saved search loads (or hard-navigates).
      props.onClose?.();
    },
    // oxlint-disable-next-line react/exhaustive-deps -- react19-baseline: pre-existing, do not copy. Add the missing dependencies rather than suppressing
    [props.onApplySavedSearch, props.onClose],
  );

  return (
    <Col className="h-full w-full overflow-x-hidden overflow-y-scroll bg-od-bg-primary">
      <Row className="items-baseline justify-between px-9 py-3 max-lg:px-3">
        <Text as="p" className="text-label-lg lg:hidden">
          Filters
        </Text>
        <Button
          variant="secondary"
          aria-label="Close filters"
          analyticsName="close-property-search-filters"
          onClick={props.onClose}
          className="size-9 rounded-full border-od-border-tertiary p-0"
        >
          <IconX size={24} />
        </Button>
      </Row>
      {props.onApplySavedSearch && (
        <Col className="items-start px-9 pb-3 max-lg:px-6">
          <SavedSearchesDropdown
            onApplySavedSearch={handleApplySavedSearch}
            activeSavedSearchId={props.activeSavedSearch?.id}
          />
        </Col>
      )}
      <Col className="px-9 pb-3 pt-6 max-lg:px-6">
        <SearchFilters {...props} key={`${resetFiltersKey}-${props.minBeds}-${props.minBaths}`} />
      </Col>
      <Row className="sticky bottom-0 z-[1] w-full flex-wrap items-center justify-end gap-2 bg-od-bg-primary px-6 py-3 max-md:justify-between max-md:px-4">
        {props.saveSearchQuery && (
          <SaveSearchButton
            query={props.saveSearchQuery}
            variant="secondary"
            activeSavedSearch={props.activeSavedSearch}
            locationLabel={props.locationLabel}
            onSaved={props.onSavedSearchCreated}
          />
        )}
        <Button
          analyticsName="property-search-filters-reset"
          aria-label="Reset filters"
          onClick={handleResetFilters}
          variant="secondary"
          className="min-w-38 max-md:min-w-0 max-md:flex-1"
        >
          Reset filters
        </Button>
        <Button
          analyticsName="property-search-filters"
          aria-label="Search"
          onClick={props.onApplyFilters}
          variant="primary"
          loading={props.isLoading}
          disabled={searchDisabled}
          className="min-w-38 max-md:min-w-0 max-md:grow max-md:basis-full"
        >
          Search
        </Button>
      </Row>
    </Col>
  );
};
